import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  branches as fallbackBranches,
  company as fallbackCompany,
  complaintTypes as fallbackComplaints,
  contactDesks as fallbackDesks,
  contactTopics as fallbackTopics,
  habits as fallbackHabits,
  people as fallbackPeople,
  serviceNeeds as fallbackNeeds,
  services as fallbackServices,
  states as fallbackStates,
  steps as fallbackSteps,
} from "./data/content";
import { websiteService } from "../services/websiteService";

const FALLBACK_COPY = {
  services_kicker: "Services",
  services_title: "What we move, and how it gets to you",
  gallery_kicker: "Cargo in practice",
  gallery_title: "Handled branch to branch",
  steps_kicker: "How it works",
  steps_title: "Four steps from booking to delivery",
  branch_kicker: "Branch locator",
  branch_title: "Find your nearest Essaar branch",
  branch_body:
    "Addresses, phone numbers and working hours for every booking and delivery branch, searchable by city or state.",
  cta_quote_title: "Need a quote or a pickup?",
  cta_quote_body: "Tell us what you are sending and where it needs to go. A branch will call you back.",
  cta_issue_title: "Something not right?",
  cta_issue_body:
    "Delay, damage, shortage or a billing question. Register it with your LR number and we will follow up.",
  people_title: "The people running Essaar",
  habits_kicker: "How we work",
  habits_title: "Three habits behind every consignment",
};

export const staticSiteContent = {
  ready: false,
  company: {
    ...fallbackCompany,
    headOfficeNote: "Street address and pincode to be updated",
    helplineHours: "Days and hours to be updated",
    whatsappNumber: "9244228896",
    whatsappDisplay: "92442 28896",
    heroEyebrow: "Essaar Logistics · Coimbatore",
    heroTitle: "Send it with Essaar.",
    heroSubtitle: "Track it to the door.",
    heroBody:
      "Parcels and part loads between our branches in Tamil Nadu, Karnataka, Kerala and Puducherry. Every consignment gets a lorry receipt and a QR code you can scan to follow it.",
    yearsInService: 25,
    aboutKicker: "Who we are",
    aboutHeadline: "A Coimbatore firm that moves people and cargo across South India",
    aboutTitle: "Essaar Transport & Logistics",
    aboutBody:
      "Essaar is a partnership firm headquartered in Coimbatore, Tamil Nadu. We run a fleet of AC and Non-AC sleeper and seater coaches across Tamil Nadu, Karnataka, Kerala and Puducherry, and a cargo service for parcels and part loads.",
    aboutNote:
      "A short note on how Essaar started, who founded it, and what the firm believes about service will be added here.",
    glanceHeadquarters: "Coimbatore",
    glanceFirmType: "Partnership",
    glanceCoachFleet: "70 to 80 buses",
    glanceSince: "Year to be updated",
    mapQuery: "Essaar Transport Logistics Coimbatore",
  },
  copy: FALLBACK_COPY,
  highlights: ["Door pickup & delivery", "GST bill, e-way bill support", "Tracking by QR"],
  services: fallbackServices,
  steps: fallbackSteps,
  habits: fallbackHabits,
  people: fallbackPeople,
  desks: fallbackDesks,
  branches: fallbackBranches,
  states: fallbackStates,
  branchCount: fallbackBranches.length,
  stateCount: fallbackStates.length,
  serviceNeeds: fallbackNeeds,
  complaintTypes: fallbackComplaints,
  contactTopics: fallbackTopics,
};

function fromApi(data) {
  const branches = (data.branches || []).map((branch) => ({
    id: branch.id,
    name: branch.name,
    city: branch.city,
    state: branch.state,
    type: branch.branch_type,
    address: branch.address,
    phone: branch.phone,
    hours: branch.hours,
    latitude: branch.latitude,
    longitude: branch.longitude,
  }));
  const states = data.states?.length ? data.states : [...new Set(branches.map((branch) => branch.state))];
  return {
    ...staticSiteContent,
    ready: true,
    branches,
    states,
    branchCount: data.branch_count ?? branches.length,
    stateCount: data.state_count ?? states.length,
  };
}

const SiteContentContext = createContext(staticSiteContent);

const PUBLIC_SITE_PATHS = ["/about", "/branches", "/contact-us", "/service-enquiry", "/complaints", "/services"];

function isPublicSitePath(pathname) {
  if (pathname === "/") return true;
  return PUBLIC_SITE_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`));
}

export function SiteContentProvider({ children }) {
  const { pathname } = useLocation();
  const [content, setContent] = useState(staticSiteContent);
  const onPublicSite = isPublicSitePath(pathname);

  useEffect(() => {
    if (!onPublicSite) return undefined;
    let cancelled = false;
    websiteService
      .getPublic()
      .then((response) => {
        if (!cancelled && response.data?.data) {
          setContent(fromApi(response.data.data));
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [onPublicSite]);

  const value = useMemo(() => content, [content]);
  return <SiteContentContext.Provider value={value}>{children}</SiteContentContext.Provider>;
}

export function useSiteContent() {
  return useContext(SiteContentContext);
}
